import {
  IconDownload,
  IconEye,
  IconFileSpreadsheet,
  IconFileText,
  IconInfoCircle,
  IconPhoto,
  IconSearch,
  IconSparkles,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { ErrorState } from "@/components/shared/error-state";
import { Button, buttonVariants } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { STRATEGY_PRODUCT } from "@/mocks/strategy-product";
import { useAuthStore } from "@/stores/auth";
import { GENERIC_REPORT_URL, ReportLink } from "./ReportLink";
import { StrategyAssetButton } from "./StrategyAssetButton";
import { shopProductQuery, type useStrategyWizard } from "./use-strategy-wizard";

const GENERIC_KEYWORD_URL = "/fixtures/keyword-screening.xlsx";

interface ResearchStepProps {
  fileName: string;
  keywordFileName: string;
  onPreviewKeywords: () => void;
  wizard: ReturnType<typeof useStrategyWizard>;
}

export function ResearchStep({
  fileName,
  keywordFileName,
  onPreviewKeywords,
  wizard,
}: ResearchStepProps) {
  const busy = wizard.busy !== null;
  return (
    <section className="mx-auto flex w-full max-w-[820px] flex-col gap-6 pt-[34px]">
      <div>
        <h2 className="text-lg font-semibold leading-[26px]">
          {wizard.reportReady ? "商品分析资料已就绪" : "先了解你要推广的商品"}
        </h2>
        <p className="mt-1.5 text-[13px] leading-[21px] text-muted-foreground">
          {wizard.reportReady
            ? "先查看运营报告与关键词筛选，再确定本轮策略目标。"
            : "输入 ASIN 或亚马逊商品详情页链接，获取赛道洞察报告。"}
        </p>
      </div>
      {!wizard.reportReady ? (
        <form onSubmit={wizard.generateReport} noValidate>
          <FieldGroup>
            <Field className="gap-2" invalid={Boolean(wizard.productForm.formState.errors.product)}>
              <FieldLabel htmlFor="strategy-product">商品链接或 ASIN</FieldLabel>
              <div className="flex w-full items-center gap-3">
                <InputGroup className="rounded-[8px] shadow-none">
                  <InputGroupAddon>
                    <IconSearch className="size-[18px]" stroke={1.75} aria-hidden />
                  </InputGroupAddon>
                  <InputGroupInput
                    className="h-10"
                    id="strategy-product"
                    placeholder="请输入亚马逊详情页链接或者 ASIN"
                    disabled={busy}
                    aria-invalid={wizard.productForm.formState.errors.product ? true : undefined}
                    aria-describedby="strategy-product-help strategy-product-error"
                    {...wizard.productForm.register("product")}
                  />
                </InputGroup>
                <Button type="submit" disabled={busy}>
                  {busy ? (
                    <Spinner data-icon="inline-start" />
                  ) : (
                    <IconSparkles data-icon="inline-start" />
                  )}
                  {busy ? "正在生成…" : "生成调研报告"}
                </Button>
              </div>
              <FieldDescription id="strategy-product-help">
                支持 Amazon.com 的 /dp/ 与 /gp/product/ 链接，或 10 位 ASIN。
              </FieldDescription>
              {wizard.productForm.formState.errors.product && (
                <p
                  id="strategy-product-error"
                  role="alert"
                  className="text-xs text-destructive-foreground"
                >
                  {wizard.productForm.formState.errors.product.message}
                </p>
              )}
            </Field>
          </FieldGroup>
        </form>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3">
            <ProductSummary asin={wizard.asin} />
            <Button variant="outline" onClick={wizard.changeProduct}>
              更换商品
            </Button>
          </div>
          <div className="flex items-center gap-4 rounded-[10px] border border-line p-6">
            <IconFileText className="size-8 shrink-0 text-muted-foreground" aria-hidden />
            <div className="flex-1">
              <p className="text-sm font-medium">{fileName}</p>
              <p className="text-xs text-muted-foreground">PDF</p>
            </div>
            <ReportLink asin={wizard.asin} />
            {wizard.asin === STRATEGY_PRODUCT.asin ? (
              <StrategyAssetButton
                kind="report"
                action="download"
                label="下载"
                fileName={fileName}
              />
            ) : (
              <a
                className={buttonVariants({ variant: "outline" })}
                href={GENERIC_REPORT_URL}
                download={fileName}
              >
                <IconDownload data-icon="inline-start" />
                下载
              </a>
            )}
          </div>
          <div className="flex items-center gap-4 rounded-[10px] border border-line p-6">
            <IconFileSpreadsheet className="size-8 shrink-0 text-muted-foreground" aria-hidden />
            <div className="flex-1">
              <p className="text-sm font-medium">{keywordFileName}</p>
              <p className="text-xs text-muted-foreground">Excel</p>
            </div>
            <Button variant="outline" onClick={onPreviewKeywords}>
              <IconEye data-icon="inline-start" />
              预览
            </Button>
            {wizard.asin === STRATEGY_PRODUCT.asin ? (
              <StrategyAssetButton
                kind="keywords"
                action="download"
                label="下载"
                fileName={keywordFileName}
              />
            ) : (
              <a
                className={buttonVariants({ variant: "outline" })}
                href={GENERIC_KEYWORD_URL}
                download={keywordFileName}
              >
                <IconDownload data-icon="inline-start" />
                下载
              </a>
            )}
          </div>
        </>
      )}
      {!wizard.reportReady && wizard.busy !== "report" && !wizard.error && (
        <div className="flex items-center gap-3 rounded-[8px] border border-line bg-muted px-4 py-3 text-[13px] leading-[21px]">
          <IconInfoCircle className="size-4 shrink-0" stroke={1.75} aria-hidden />
          完成商品分析后，可继续设置业务目标。
        </div>
      )}
      {wizard.busy === "report" && (
        <div role="status" className="flex items-center gap-3 rounded-lg bg-muted/50 p-5">
          <Spinner />
          <div>
            <p className="text-sm font-medium">正在载入商品分析资料</p>
            <p className="text-xs text-muted-foreground">
              正在准备运营报告与关键词筛选，预计需要 3～5 分钟，请稍候。
            </p>
          </div>
        </div>
      )}
      {wizard.error && (
        <ErrorState
          message={wizard.error}
          onRetry={() => void wizard.generateReport()}
          variant="inline"
        />
      )}
      <div className={wizard.reportReady ? "pt-3" : "border-t border-line py-6"}>
        <h3
          className={cn(
            "font-semibold",
            wizard.reportReady ? "mb-1.5 text-base leading-6" : "mb-[18px] text-lg leading-[26px]",
          )}
        >
          {wizard.reportReady ? "下一步：把调研转化为运营目标" : "一份报告，建立决策起点"}
        </h3>
        {wizard.reportReady ? (
          <p className="text-sm text-muted-foreground">
            选择业务目标和执行模式，查看未来 14 天预估与推荐打法。
          </p>
        ) : (
          <div className="flex flex-col gap-[18px]">
            {[
              ["商品与市场", "商品卖点、细分市场与竞品定位"],
              ["流量与广告", "关键词、定向 ASIN 与广告机会"],
              ["经营与打法", "财务、备货、风险与推广建议"],
            ].map(([title, body], i) => (
              <div
                className="flex h-[38px] items-center gap-3 text-[13px] leading-[22px]"
                key={title}
              >
                <span className="w-9 shrink-0 text-muted-foreground">0{i + 1}</span>
                <span className="w-[150px] shrink-0 font-medium">{title}</span>
                <span className="text-muted-foreground">{body}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProductSummary({ asin }: { asin: string }) {
  const shopId = useAuthStore((s) => s.currentShopId);
  // `isLoading` (not `isPending`) so a disabled query without a shop shows the fallback text.
  const { data, isLoading } = useQuery({
    ...shopProductQuery(shopId ?? "", asin),
    enabled: Boolean(shopId) && asin !== STRATEGY_PRODUCT.asin,
  });
  const listing = data?.listing ?? null;

  return (
    <div className="flex min-w-0 items-center gap-3.5">
      <span className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-line bg-muted">
        {listing?.imageUrl || asin === STRATEGY_PRODUCT.asin ? (
          <img
            src={listing?.imageUrl ?? STRATEGY_PRODUCT.imageUrl}
            alt=""
            className="size-full object-contain"
          />
        ) : (
          <IconPhoto className="size-6 text-muted-foreground" stroke={1.5} aria-hidden />
        )}
      </span>
      <div className="min-w-0">
        {isLoading && asin !== STRATEGY_PRODUCT.asin ? (
          <Skeleton className="h-6 w-72" />
        ) : (
          <p
            className={cn(
              "truncate text-base leading-6",
              listing?.title || asin === STRATEGY_PRODUCT.asin
                ? "font-semibold"
                : "text-muted-foreground",
            )}
          >
            {listing?.title ??
              (asin === STRATEGY_PRODUCT.asin ? STRATEGY_PRODUCT.title : "未在当前店铺找到该商品")}
          </p>
        )}
        <p className="mt-1 text-xs leading-5 text-muted-foreground">ASIN {asin}</p>
      </div>
    </div>
  );
}
